<template>
  <v-dialog v-model="dialog" max-width="850px" persistent @keydown.esc="close">
    <v-card>
      <v-toolbar dark color="primary">
        <v-icon class="mr-2">qr_code</v-icon>
        <v-toolbar-title>Crear Código de Acceso QR</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon color="red" @click="close">
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-4">
        <!-- Info reserva -->
        <v-layout row wrap class="mb-2">
          <v-flex xs12 sm6 class="pr-2">
            <v-text-field
              :value="reservation && reservation.owner_company_name"
              label="Empresa dueña"
              prepend-icon="business"
              readonly
              hide-details
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 class="pl-2">
            <v-text-field
              :value="formatReservationPeriod()"
              label="Período de la reserva"
              prepend-icon="date_range"
              readonly
              hide-details
            ></v-text-field>
          </v-flex>
        </v-layout>

        <v-divider class="my-3"></v-divider>

        <!-- Selector: Nuevo Transportista o existente -->
        <div class="mb-4">
          <p class="subheading grey--text mb-2">Tipo de visitante</p>
          <v-radio-group v-model="providerMode" row mandatory>
            <v-radio label="Transportista existente" value="existing"></v-radio>
            <v-radio label="Nuevo Transportista" value="new"></v-radio>
          </v-radio-group>
        </div>

        <v-divider class="my-3"></v-divider>

        <v-form ref="form" v-model="formValid">
          <v-layout row wrap>

            <!-- Columna izquierda -->
            <v-flex xs12 md6 class="pr-3">
              <p class="subheading grey--text mb-2">Datos del acceso</p>

              <v-select
                v-model="formData.parking_space_id"
                :items="availableSpaces"
                item-text="display_name"
                item-value="parking_space"
                label="Cajón de parqueo"
                prepend-icon="local_parking"
                :rules="[rules.required]"
              ></v-select>

              <!-- OPCIÓN A: Seleccionar Transportista existente -->
              <v-autocomplete
                v-if="providerMode === 'existing'"
                v-model="formData.provider_id"
                :items="providers"
                item-text="display_name"
                item-value="id"
                label="Seleccionar Transportista"
                prepend-icon="person"
                :rules="[rules.required]"
                @change="onProviderSelected"
              >
                <template v-slot:item="data">
                  <v-list-tile-content>
                    <v-list-tile-title>{{ data.item.display_name }}</v-list-tile-title>
                    <v-list-tile-sub-title>{{ data.item.telephone }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                </template>
              </v-autocomplete>

              <!-- OPCIÓN B: Crear nuevo Transportista -->
              <template v-if="providerMode === 'new'">
                <v-text-field
                  v-model="formData.first_name"
                  label="Nombre"
                  prepend-icon="person"
                  :rules="[rules.required]"
                ></v-text-field>

                <v-text-field
                  v-model="formData.last_name"
                  label="Apellido (opcional)"
                  prepend-icon="person"
                ></v-text-field>
              </template>
            </v-flex>

            <!-- Columna derecha -->
            <v-flex xs12 md6 class="pl-3">
              <p class="subheading grey--text mb-2">Contacto y vehículo</p>

              <v-text-field
                v-model="formData.email"
                label="Email (opcional)"
                prepend-icon="email"
                type="email"
              ></v-text-field>

              <v-layout row align-center>
                <v-flex xs5 class="pr-2">
                  <v-select
                    v-model="formData.phone_code"
                    :items="phoneCodes"
                    item-text="label"
                    item-value="code"
                    label="Lada"
                    prepend-icon="flag"
                  ></v-select>
                </v-flex>
                <v-flex xs7>
                  <v-text-field
                    v-model="formData.phone"
                    label="Teléfono"
                    prepend-icon="phone"
                    type="tel"
                  ></v-text-field>
                </v-flex>
              </v-layout>

              <v-text-field
                v-model="formData.plates"
                label="Placas (opcional)"
                prepend-icon="directions_car"
              ></v-text-field>

              <v-text-field
                v-model="formData.unit_specs"
                label="Modelo / Especificaciones (opcional)"
                prepend-icon="local_shipping"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <v-divider class="my-3"></v-divider>

          <v-layout row wrap>
            <!-- Vigencia -->
            <v-flex xs12 md6 class="pr-3">
              <p class="subheading grey--text mb-2">Vigencia del código</p>

              <v-menu ref="startMenu" v-model="startMenu" :close-on-content-click="false" offset-y lazy>
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="formData.invitation_date"
                    label="Fecha inicio"
                    prepend-icon="event"
                    readonly
                    :rules="[rules.required, rules.dateInReservation]"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="formData.invitation_date" @input="startMenu = false"></v-date-picker>
              </v-menu>

              <v-menu ref="endMenu" v-model="endMenu" :close-on-content-click="false" offset-y lazy>
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="formData.expiration"
                    label="Fecha fin"
                    prepend-icon="event"
                    readonly
                    :rules="[rules.required, rules.expirationAfterStart, rules.dateInReservation]"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="formData.expiration" @input="endMenu = false"></v-date-picker>
              </v-menu>
            </v-flex>

            <!-- Descripción -->
            <v-flex xs12 md6 class="pl-3">
              <p class="subheading grey--text mb-2">Descripción</p>

              <v-text-field
                v-model="formData.subject"
                label="Asunto / Descripción"
                prepend-icon="note"
                counter="256"
              ></v-text-field>

              <v-checkbox
                v-model="formData.activate_immediately"
                label="Activar código inmediatamente"
              ></v-checkbox>
            </v-flex>
          </v-layout>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-btn color="primary" @click="close">Cancelar</v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" :loading="loading" @click="submit">
          Crear Código QR
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'
import store from '@/store'
import { mapGetters } from 'vuex'
import { extractErrorMessage, getErrorColor, handleApiError } from '@/utils/errorHandler'

export default {
  name: 'QRCodeDialog',
  props: {
    dialog: {
      type: Boolean,
      required: true
    },
    reservation: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      providerMode: 'existing',
      providers: [],
      loadingProviders: false,
      formValid: false,
      startMenu: false,
      endMenu: false,
      formData: {
        reservation_id: null,
        parking_space_id: null,
        provider_id: null,
        invitation_date: '',
        expiration: '',
        subject: '',
        notes: '',
        first_name: '',
        last_name: '',
        email: '',
        phone_code: '+52',
        phone: '',
        plates: '',
        unit_specs: '',
        activate_immediately: false
      },
      loading: false,
      phoneCodes: [
        { code: '+52', label: '+52 MX' },
        { code: '+1', label: '+1 USA/CA' },
        { code: '+54', label: '+54 AR' },
        { code: '+55', label: '+55 BR' },
        { code: '+56', label: '+56 CL' },
        { code: '+57', label: '+57 CO' },
        { code: '+503', label: '+503 SV' },
        { code: '+502', label: '+502 GT' },
        { code: '+504', label: '+504 HN' },
        { code: '+505', label: '+505 NI' },
        { code: '+507', label: '+507 PA' },
        { code: '+51', label: '+51 PE' },
        { code: '+34', label: '+34 ES' }
      ],
      rules: {
        required: (v) => !!v || 'Campo requerido',
        expirationAfterStart: (v) => {
          if (!v || !this.formData.invitation_date) return true
          return v >= this.formData.invitation_date || 'Debe ser mayor o igual a fecha inicio'
        },
        dateInReservation: (v) => {
          if (!v) return true
          const date = new Date(v)
          const start = new Date(this.reservation.start_date)
          const end = new Date(this.reservation.end_date)
          if (date < start || date > end) {
            return 'La fecha debe estar dentro del período de la reserva'
          }
          return true
        }
      }
    }
  },
  created() {
    this.fetchProviders()
  },
  computed: {
    ...mapGetters(['providerHasParkingModule', 'getProviderParkingModule', 'getProfile']),
    providerCompanyId() {
      if (this.getProfile && this.getProfile.id_company) {
        return this.getProfile.id_company
      }
      if (this.getProviderParkingModule && this.getProviderParkingModule.provider_empresa_id) {
        return this.getProviderParkingModule.provider_empresa_id
      }
      return null
    },
    availableSpaces() {
      const spaces = (this.reservation && this.reservation.reserved_spaces) || []
      return spaces.map(s => ({
        ...s,
        display_name: 'Cajón ' + (s.number || s.space_number) + (s.space_type_name ? ' - ' + s.space_type_name : '')
      }))
    }
  },
  watch: {
    dialog(val) {
      if (val) {
        this.initializeForm()
        // Asegurar que reservation_id se establece correctamente
        if (this.reservation && this.reservation.id) {
          this.formData.reservation_id = this.reservation.id
        }
        this.fetchProviders()
      }
    },
    reservation(newVal) {
      if (newVal && newVal.id) {
        this.formData.reservation_id = newVal.id
      }
    }
  },
  methods: {
    fetchProviders() {
      this.loadingProviders = true
      const params = {}
      if (this.providerCompanyId) {
        params.idEmpresa = this.providerCompanyId
      }
      const config = {
        method: 'get',
        url: 'https://empresa-service.intrare.app/Parking/providers/',
        params: params,
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        }
      }

      axios(config)
        .then((response) => {
          const rows = Array.isArray(response.data) ? response.data : []
          this.providers = rows.map(p => ({
            ...p,
            display_name: `${p.first_name} ${p.last_name}` + (p.plates ? ` (${p.plates})` : '')
          }))
        })
        .catch((error) => {
          console.error('Error cargando Transportistas:', error)
          handleApiError(error, store)
          this.providers = []
        })
        .finally(() => {
          this.loadingProviders = false
        })
    },

    onProviderSelected(providerId) {
      const provider = this.providers.find(p => p.id === providerId)
      if (provider) {
        this.formData.first_name = provider.first_name
        this.formData.last_name = provider.last_name || ''
        this.formData.email = provider.email || ''
        this.formData.plates = provider.plates || ''
        this.formData.unit_specs = provider.unit_specs || ''

        // Separar el lada del número: el teléfono en BD puede traer el lada incluido
        const phoneCode = provider.phone_code || '+52'
        let phone = provider.telephone || ''
        if (phone.startsWith(phoneCode)) {
          phone = phone.slice(phoneCode.length)
        }
        this.formData.phone_code = phoneCode
        this.formData.phone = phone
      }
    },

    initializeForm() {
      this.startMenu = false
      this.endMenu = false
      this.providerMode = 'existing'

      const today = new Date().toISOString().split('T')[0]

      this.formData = {
        reservation_id: this.reservation && this.reservation.id,
        parking_space_id: null,
        provider_id: null,
        invitation_date: today,
        expiration: (this.reservation && this.reservation.end_date) || today,
        subject: 'Acceso de Transportista',
        notes: '',
        first_name: '',
        last_name: '',
        email: '',
        phone_code: '+52',
        phone: '',
        plates: '',
        unit_specs: '',
        activate_immediately: false
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr + 'T00:00:00')
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    },

    formatTime(timeStr) {
      if (!timeStr) return ''
      // Esperando formato HH:MM:SS o HH:MM
      const parts = timeStr.split(':')
      return parts[0] + ':' + parts[1]
    },

    formatReservationPeriod() {
      if (!this.reservation) return ''
      
      let period = this.formatDate(this.reservation.start_date) + ' — ' + this.formatDate(this.reservation.end_date)
      
      // Agregar horario si existe
      if (this.reservation.start_time || this.reservation.end_time) {
        const startTime = this.reservation.start_time ? this.formatTime(this.reservation.start_time) : '--:--'
        const endTime = this.reservation.end_time ? this.formatTime(this.reservation.end_time) : '--:--'
        period += ' | ' + startTime + ' — ' + endTime
      }
      
      return period
    },

    async submit() {
      if (!this.$refs.form.validate()) {
        return
      }

      if (!this.formData.email && !this.formData.phone) {
        store.commit('snackbar_change_message', 'Debes proporcionar al menos email o teléfono')
        store.commit('snackbar_change_color', 'warning')
        store.commit('snackbar_activate_true')
        return
      }

      if (!this.formData.parking_space_id) {
        store.commit('snackbar_change_message', 'Selecciona un cajón')
        store.commit('snackbar_change_color', 'warning')
        store.commit('snackbar_activate_true')
        return
      }

      if (this.providerMode === 'existing' && !this.formData.provider_id) {
        store.commit('snackbar_change_message', 'Selecciona un Transportista')
        store.commit('snackbar_change_color', 'warning')
        store.commit('snackbar_activate_true')
        return
      }

      this.loading = true

      try {
        let payload = { ...this.formData }
        // Combinar lada + teléfono en un solo campo
        if (payload.phone) {
          payload.phone = payload.phone_code + payload.phone
        }
        delete payload.phone_code
        
        // Asegurar que reservation_id tenga el valor correcto
        if (!payload.reservation_id && this.reservation && this.reservation.id) {
          payload.reservation_id = this.reservation.id
        }
        
        // Si reutiliza Transportista, elimina los datos de nombres
        if (this.providerMode === 'existing') {
          delete payload.first_name
          delete payload.last_name
        } else {
          delete payload.provider_id
        }
        
        // Elimina campos vacíos
        Object.keys(payload).forEach(function(key) {
          if (payload[key] === undefined || payload[key] === null || payload[key] === '') {
            delete payload[key]
          }
        })

        const config = {
          method: 'post',
          url: 'https://empresa-service.intrare.app/Parking/invitations/reservations/codes/create/',
          headers: {
            'Content-Type': 'application/json',
            'authorization': 'Token ' + store.getters.getToken
          },
          data: payload
        }

        console.log('Creando código QR:', config)
        await axios(config)
        
        // Mostrar éxito en snackbar
        store.commit('snackbar_change_message', 'Código QR creado exitosamente')
        store.commit('snackbar_change_color', 'success')
        store.commit('snackbar_activate_true')
        
        this.$emit('created')
      } catch (err) {
        // Usar el manejador centralizado de errores
        handleApiError(err, store)
      } finally {
        this.loading = false
      }
    },

    close() {
      this.$emit('close')
    }
  }
}
</script>

